import { type ReactNode, useEffect, useRef, useState } from "react";

/** replaces the spinner of the full-screen overlay shown while `isLoading` is true */
export type LoadingScreen = {
  content: ReactNode;
  /** keep the overlay up for at least this long, so short loads don't flash */
  minDurationMs?: number;
  /** fade the overlay out over this long once loading is done */
  fadeOutMs?: number;
};

type Phase = "idle" | "holding" | "fading";

/**
 * derives the overlay's visibility from `isLoading`, including the hold and fade after it ends.
 * `startupScreen` is used for loads flagged with `isStarting`, `loadingScreen` for all others.
 */
export function useLoadingOverlay(
  { isLoading, isStarting }: { isLoading: boolean; isStarting?: boolean },
  loadingScreen?: LoadingScreen,
  startupScreen?: LoadingScreen
) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [wasLoading, setWasLoading] = useState(isLoading);
  // decided when a load begins, so the screen stays the same through its hold and fade
  const [isStartup, setIsStartup] = useState(Boolean(isStarting));
  const loadingSince = useRef<number | null>(null);

  const screen = (isStartup ? startupScreen : undefined) ?? loadingScreen;
  const minDurationMs = screen?.minDurationMs ?? 0;
  const fadeOutMs = screen?.fadeOutMs ?? 0;
  const lingers = minDurationMs > 0 || fadeOutMs > 0;

  // adjusted while rendering, so the overlay doesn't disappear for a frame when `isLoading` turns false
  if (isLoading !== wasLoading) {
    setWasLoading(isLoading);
    setPhase(!isLoading && lingers ? "holding" : "idle");
    if (isLoading && phase === "idle") {
      setIsStartup(Boolean(isStarting));
    }
  }

  useEffect(() => {
    if (isLoading) {
      // a new load during the hold keeps the original start, so the minimum isn't extended
      loadingSince.current ??= Date.now();
      return;
    }
    if (loadingSince.current === null) {
      return;
    }
    if (!lingers) {
      loadingSince.current = null;
      return;
    }

    const remaining = Math.max(
      minDurationMs - (Date.now() - loadingSince.current),
      0
    );
    const fade = setTimeout(() => setPhase("fading"), remaining);
    const done = setTimeout(() => {
      loadingSince.current = null;
      setPhase("idle");
    }, remaining + fadeOutMs);

    return () => {
      clearTimeout(fade);
      clearTimeout(done);
    };
  }, [isLoading, lingers, minDurationMs, fadeOutMs]);

  return {
    screen,
    isOverlayShown: isLoading || phase !== "idle",
    isOverlayFading: !isLoading && phase === "fading",
    // the content is already visible underneath while the overlay fades out
    isContentHidden: isLoading || phase === "holding",
  };
}
